import { BRANDS, type BrandId, type LogoAsset } from '../brand/brands';

/**
 * BrandLockup (docs/design-system/06-COMPONENTS-AND-STATES.md). Always renders the master asset at
 * its intrinsic ratio: never stretched, recoloured or retyped. The only brand without a supplied
 * file is Research Space, whose guideline defines the mark by construction (PDF p. 2): a rounded
 * square with a 27% radius holding the title-face "M" at 55% of the square height.
 */

export type LogoSurface = 'stage' | 'dark';

const escape = (value: string): string => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

function asset(id: BrandId, surface: LogoSurface): LogoAsset {
  const b = BRANDS[id];
  return surface === 'dark' ? b.logoOnDark : b.logo;
}

/**
 * Research Space monogram, built to the PDF construction. `onField` is the reversed fill used on
 * the brand field and, by extension, on any dark stage (white square, #37474F letter).
 */
export function researchMonogram(size: number, onField = false): string {
  const fill = onField ? '#FFFFFF' : '#37474F';
  const ink = onField ? '#37474F' : '#FFFFFF';
  return `<svg class="rs-monogram" width="${size}" height="${size}" viewBox="0 0 100 100" aria-hidden="true">
    <rect width="100" height="100" rx="27" fill="${fill}"/>
    <text x="50" y="50" dy=".35em" text-anchor="middle" fill="${ink}" font-family="var(--rs-serif)" font-weight="700" font-size="55">M</text>
  </svg>`;
}

/** Research Space horizontal lockup: monogram + "Moderta AI" in the title face, product line in the compact face. */
function researchLockup(height: number, onField: boolean): string {
  return `<span class="rs-lockup${onField ? ' rs-lockup--field' : ''}" style="--h:${height}px" role="img" aria-label="Moderta AI — Research Space">
    ${researchMonogram(height, onField)}
    <span class="rs-lockup__text"><b>Moderta AI</b><small>Research Space</small></span>
  </span>`;
}

/** HTML lockup sized by height. */
export function brandLogo(id: BrandId, { height = 64, surface = 'stage', className = '' }: { height?: number; surface?: LogoSurface; className?: string } = {}): string {
  const a = asset(id, surface);
  if (a.src === 'spec') return `<span class="brand-logo brand-logo--${id} ${className}">${researchLockup(height, surface === 'dark')}</span>`;
  const width = Math.round(height * a.ratio);
  return `<img class="brand-logo brand-logo--${id} ${className}" src="${a.src}" alt="${escape(a.alt)}" width="${width}" height="${height}" style="--h:${height}px" decoding="async" draggable="false">`;
}

/**
 * The same lockup inside a 1600×900 diagram. `anchor` positions it like SVG text. MINT's master
 * is drawn on a light plate when it sits on a dark stage, so #257D5B is never placed on near-black.
 */
export function brandLogoSvg(
  id: BrandId,
  { x, y, height, anchor = 'middle', surface = 'dark', className = '' }: { x: number; y: number; height: number; anchor?: 'start' | 'middle' | 'end'; surface?: LogoSurface; className?: string },
): string {
  const a = asset(id, surface);
  if (a.src === 'spec') {
    // Monogram + title-face name as SVG primitives so the lockup scales with the diagram.
    const onField = surface === 'dark';
    const textWidth = height * 2.9;
    const width = height + height * 0.32 + textWidth;
    const left = anchor === 'start' ? x : anchor === 'end' ? x - width : x - width / 2;
    return `<g class="brand-logo-svg brand-logo-svg--${id} ${className}" role="img" aria-label="${escape(a.alt)}">
      <rect x="${left}" y="${y - height / 2}" width="${height}" height="${height}" rx="${height * 0.27}" fill="${onField ? '#FFFFFF' : '#37474F'}"/>
      <text x="${left + height / 2}" y="${y}" dy=".35em" text-anchor="middle" fill="${onField ? '#37474F' : '#FFFFFF'}" font-family="var(--rs-serif)" font-weight="700" font-size="${height * 0.55}">M</text>
      <text class="rs-svg-name" x="${left + height * 1.32}" y="${y - height * 0.06}" font-family="var(--rs-serif)" font-weight="700" font-size="${height * 0.5}">Moderta AI</text>
      <text class="rs-svg-sub" x="${left + height * 1.32}" y="${y + height * 0.42}" font-family="var(--rs-sans)" font-size="${height * 0.3}">Research Space</text>
    </g>`;
  }
  const width = height * a.ratio;
  const left = anchor === 'start' ? x : anchor === 'end' ? x - width : x - width / 2;
  const plate = id.startsWith('mint') && surface === 'dark'
    ? `<rect class="brand-logo-svg__plate" x="${left - height * 0.55}" y="${y - height * 1.05}" width="${width + height * 1.1}" height="${height * 2.1}" rx="${height * 0.5}"/>`
    : '';
  return `<g class="brand-logo-svg brand-logo-svg--${id} ${className}">
    ${plate}
    <image href="${a.src}" x="${left.toFixed(1)}" y="${(y - height / 2).toFixed(1)}" width="${width.toFixed(1)}" height="${height}" preserveAspectRatio="xMidYMid meet"><title>${escape(a.alt)}</title></image>
  </g>`;
}
